<!-- Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. -->

<tp-multi-panel-header
	[collapsed]="collapseFilterPanel$ | async"
	(click)="userChangedFiltersCollapsedState()"
	class="plan-tickets-filter-header"
>
	<h1>{{ appStrings.PLAN_FILTER_TITLE }}</h1>
	<ng-container *ngIf="isPlanPrintEnabled$ | async">
		<button mat-button class="print-button" automationId="printButton" (click)="onOpenPlanFilter()">
			Print
			<mat-icon>print</mat-icon>
		</button>
	</ng-container>
	<mat-icon class="close-button" automationId="closeButton" (click)="onCloseFilter()">close</mat-icon>
</tp-multi-panel-header>
<tp-multi-panel-content>
	<mat-divider></mat-divider>
	<form [formGroup]="formGroup" class="plan-tickets-filter-form">
		<mat-extended-select
			class="filter-operator-select"
			[option$]="filterOperatorOptions$"
			[formControl]="filterOperatorControl"
			[placeholder]="appStrings.PLAN_FILTER_OPERATOR_PLACEHOLDER"
		></mat-extended-select>

		<!-- filter by description -->
		<mat-slide-toggle
			[formControl]="formGroup.controls.toggleFilterByDescription"
			automationId="toggleFilterByDescription"
			class="control-spacer"
		>
			{{ appStrings.PLAN_FILTER_BY(appStrings.PLAN_FILTER_BY_DESCRIPTION) }}
		</mat-slide-toggle>
		<mat-form-field
			*ngIf="formGroup.controls.toggleFilterByDescription.value"
			automationId="tpFilterByDescription"
			subscriptSizing="dynamic"
		>
			<mat-label>{{ appStrings.PLAN_FILTER_BY_DESCRIPTION | titlecase }}</mat-label>
			<tp-filter
				fieldType="text"
				formControlName="description"
				[excludeOperators]="[CommonPredicateOp.NotSet, CommonPredicateOp.AnyValue]"
			></tp-filter>
		</mat-form-field>

		<!-- filter by external schedule id -->
		<mat-slide-toggle
			[formControl]="formGroup.controls.toggleFilterByExternalSchedule"
			automationId="toggleFilterByExternalSchedule"
			class="control-spacer"
		>
			{{ appStrings.PLAN_FILTER_BY(appStrings.FILTER_BY_EXTERNAL_SCHEDULE_ID) }}
		</mat-slide-toggle>
		<mat-form-field
			*ngIf="formGroup.controls.toggleFilterByExternalSchedule.value"
			automationId="tpFilterByExternalSchedule"
			subscriptSizing="dynamic"
		>
			<mat-label>{{ appStrings.FILTER_BY_EXTERNAL_SCHEDULE_ID }}</mat-label>
			<tp-filter fieldType="text" formControlName="externalScheduleId"> </tp-filter>
		</mat-form-field>

		<!-- filter by wbs code -->
		<mat-slide-toggle
			[formControl]="formGroup.controls.toggleFilterByWBS"
			automationId="toggleFilterByWBS"
			class="control-spacer"
		>
			{{ appStrings.PLAN_FILTER_BY(appStrings.WBS_CODE) }}
		</mat-slide-toggle>
		<mat-form-field
			*ngIf="formGroup.controls.toggleFilterByWBS.value"
			subscriptSizing="dynamic"
			automationId="tpFilterByWBS"
		>
			<mat-label>{{ appStrings.WBS_CODE }}</mat-label>
			<tp-filter fieldType="text" formControlName="wbsCode"> </tp-filter>
		</mat-form-field>

		<!-- filter by attachment comment -->
		<mat-slide-toggle
			[formControl]="formGroup.controls.toggleFilterByAttachmentComment"
			automationId="toggleFilterByAttachmentComment"
			class="control-spacer"
		>
			{{ appStrings.PLAN_FILTER_BY(appStrings.PLAN_FILTER_BY_ATTACHMENT_COMMENT) }}
		</mat-slide-toggle>
		<mat-form-field
			*ngIf="formGroup.controls.toggleFilterByAttachmentComment.value"
			automationId="tpFilterByAttachmentComment"
			subscriptSizing="dynamic"
		>
			<mat-label>{{ appStrings.PLAN_FILTER_BY_ATTACHMENT_COMMENT }}</mat-label>
			<tp-filter fieldType="text" formControlName="attachmentComment"> </tp-filter>
		</mat-form-field>

		<div class="row">
			<mat-form-field>
				<mat-label>{{ appStrings.UICONTROL_TEXT_START_DATE }}</mat-label>
				<input
					matInput
					readonly
					formControlName="fromDate"
					[matDatepicker]="asOfDatePicker"
					(click)="asOfDatePicker.open()"
					automationId="planFilterStartDate"
				/>
				<mat-error>{{ appStrings.FIELD_IS_REQUIRED }}</mat-error>
				<mat-datepicker-toggle
					automationId="asOfDatePicker"
					matSuffix
					[for]="asOfDatePicker"
				></mat-datepicker-toggle>
				<mat-datepicker touchUi #asOfDatePicker></mat-datepicker>
			</mat-form-field>

			<mat-form-field>
				<mat-label>{{ appStrings.UICONTROL_TEXT_END_DATE }}</mat-label>
				<input
					matInput
					readonly
					formControlName="toDate"
					[matDatepicker]="endDatePicker"
					(click)="endDatePicker.open()"
					automationId="planFilterEndDate"
				/>
				<mat-error>{{ appStrings.FIELD_IS_REQUIRED }}</mat-error>
				<mat-datepicker-toggle
					automationId="endDatePicker"
					matSuffix
					[for]="endDatePicker"
				></mat-datepicker-toggle>
				<mat-datepicker touchUi #endDatePicker></mat-datepicker>
			</mat-form-field>
		</div>
		<mat-extended-select
			automationId="planFilterLocations"
			formControlName="locationIds"
			placeholder="{{ appStrings.PROJECT_ACTION_LOCATIONS }}"
			[required]="false"
			[includeFilter]="true"
			[multiSelect]="true"
			[noSelectionText]="appStrings.PLAN_FILTER_ALL(appStrings.PROJECT_ACTION_LOCATIONS)"
			[option$]="locationOptions$"
			[labelPath]="['view', 'name']"
			[valuePath]="['$id']"
			[iconBackgroundColorPath]="['view', 'color']"
		>
		</mat-extended-select>

		<mat-extended-select
			automationId="planFilterRoles"
			formControlName="roleIds"
			placeholder="{{ appStrings.PROJECT_ACTION_ROLES }}"
			[required]="false"
			[includeFilter]="true"
			[multiSelect]="true"
			[noSelectionText]="appStrings.PLAN_FILTER_ALL(appStrings.PROJECT_ACTION_ROLES)"
			[option$]="rolesOptions$"
			[labelPath]="['view', 'roleName']"
			[valuePath]="['$id']"
			[iconBackgroundColorPath]="['view', 'color']"
		>
		</mat-extended-select>

		<mat-extended-select
			automationId="planFilterTicketTypes"
			formControlName="type"
			placeholder="{{ appStrings.CUSTOM_FIELD_INFO_TICKET_TYPES }}"
			[includeFilter]="false"
			[required]="false"
			[multiSelect]="true"
			[noSelectionText]="appStrings.PLAN_FILTER_ALL(appStrings.CUSTOM_FIELD_INFO_TICKET_TYPES)"
			[option$]="ticketTypeOptions$"
			[valuePath]="[]"
		>
		</mat-extended-select>

		<mat-extended-select
			automationId="planFilterTicketStatus"
			formControlName="ticketStatus"
			placeholder="{{ appStrings.PLAN_FILTER_TICKET_STATUS }}"
			[includeFilter]="false"
			[required]="false"
			[multiSelect]="true"
			[noSelectionText]="appStrings.PLAN_FILTER_ALL(appStrings.PLAN_FILTER_TICKET_STATUS)"
			[option$]="ticketStatusOptions$"
			[valuePath]="[]"
		>
		</mat-extended-select>

		<mat-extended-select
			automationId="planFilterTicketAttachments"
			formControlName="ticketAttachments"
			placeholder="{{ appStrings.PLAN_FILTER_TICKET_ATTACHMENT }}"
			[includeFilter]="false"
			[required]="false"
			[noSelectionText]="appStrings.PLAN_FILTER_TICKET_ATTACHMENT"
			[option$]="ticketAttachmentOptions$"
			[valuePath]="[]"
		>
		</mat-extended-select>

		<mat-extended-select
			automationId="planFilterTicketSwimlanes"
			formControlName="swimlaneIds"
			[placeholder]="appStrings.PROJECT_ACTION_SWIMLANES"
			[includeFilter]="false"
			[required]="false"
			[multiSelect]="true"
			[noSelectionText]="appStrings.PLAN_FILTER_ALL(appStrings.PROJECT_ACTION_SWIMLANES)"
			[option$]="swimlaneOptions$"
			[labelPath]="['view', 'label']"
			[iconBackgroundColorPath]="['view', 'color']"
			[valuePath]="['$id']"
		>
		</mat-extended-select>
		<mat-slide-toggle
			[formControl]="formGroup.controls.toggleFilterByCustomFields"
			automationId=""
			class="control-spacer"
		>
			{{ appStrings.PLAN_FILTER_BY(appStrings.FILTER_BY_CUSTOM_FIELDS) }}
		</mat-slide-toggle>

		<ng-container *ngIf="formGroup.controls.toggleFilterByCustomFields.value">
			<custom-field-filter
				automationId="customField1"
				formControlName="customField1"
				[title]="appStrings.FILTER_CUSTOM_FIELD_FILTER_1"
				[customFieldDefinitions]="customFieldOptions$"
			></custom-field-filter>
			<custom-field-filter
				automationId="customField2"
				formControlName="customField2"
				[title]="appStrings.FILTER_CUSTOM_FIELD_FILTER_2"
				[customFieldDefinitions]="customFieldOptions$"
			></custom-field-filter>
			<custom-field-filter
				automationId="customField3"
				formControlName="customField3"
				[title]="appStrings.FILTER_CUSTOM_FIELD_FILTER_3"
				[customFieldDefinitions]="customFieldOptions$"
			></custom-field-filter>
		</ng-container>
	</form>

	<mat-divider></mat-divider>

	<div class="plan-tickets-filter-footer">
		<button
			mat-button
			type="button"
			color="primary"
			automationId="planFilterClear"
			class="plan-filter-clear-btn"
			(click)="onClearSettings()"
		>
			{{ appStrings.UICONTROL_TEXT_CLEAR_SETTINGS | uppercase }}
		</button>
	</div>
</tp-multi-panel-content>
